import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, ClipboardCheck, GitBranch, ChevronRight } from "lucide-react";

const STATUS_STYLES = {
  DRAFT:        { bg: "rgba(148,163,184,0.14)", fg: "#94a3b8", label: "Borrador" },
  COACH_REVIEW: { bg: "rgba(250,204,21,0.14)",  fg: "#facc15", label: "En revisión" },
  APPROVED:     { bg: "rgba(59,130,246,0.14)",  fg: "#60a5fa", label: "Aprobado" },
  PUBLISHED:    { bg: "rgba(34,197,94,0.14)",   fg: "#4ade80", label: "Publicado" },
  ARCHIVED:     { bg: "rgba(115,115,115,0.14)", fg: "#a3a3a3", label: "Archivado" },
};

export default function AdminPlansV2() {
  const [plans, setPlans] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ client_id: "", name: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get("/admin/plans-v2"),
        api.get("/admin/clients"),
      ]);
      setPlans(p.data);
      setClients(c.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const createPlan = async () => {
    if (!form.client_id || !form.name.trim()) return toast.error("Cliente y nombre requeridos");
    setCreating(true);
    try {
      const { data } = await api.post("/admin/plans-v2", { ...form, weeks: [{ index: 0, label: "Semana 1", days: [] }] });
      toast.success("Plan creado");
      setForm({ client_id: "", name: "" });
      window.location.href = `/app/admin/plans-v2/${data.id}`;
    } catch (e) { toast.error(formatErr(e)); }
    finally { setCreating(false); }
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || id;

  return (
    <AppShell>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: "2rem", letterSpacing: "0.06em", margin: 0 }}>Planes V2</h1>
        <p style={{ color: "#a3a3a3", fontSize: 13, margin: "4px 0 0" }}>Builder profesional con versionado, aprobación y publicación segura.</p>
      </div>

      <div style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.08)", padding: 16, borderRadius: 4, marginBottom: 20 }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase" }}>Nuevo plan</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "end" }}>
          <div>
            <label style={labelStyle}>Cliente</label>
            <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} style={inputStyle} data-testid="plan-v2-client">
              <option value="">— Selecciona —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Nombre del plan</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Pretemporada verano" style={inputStyle} data-testid="plan-v2-name" />
          </div>
          <button onClick={createPlan} disabled={creating} style={btnPrimary} data-testid="plan-v2-create"><Plus size={14}/> Crear</button>
        </div>
      </div>

      <div style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4 }}>
        {loading && <div style={{ padding: 24, color: "#a3a3a3", textAlign: "center" }}>Cargando…</div>}
        {!loading && plans.length === 0 && <div style={{ padding: 40, color: "#a3a3a3", textAlign: "center" }}><ClipboardCheck size={32} style={{ marginBottom: 8, opacity: 0.4 }}/><div>Sin planes V2 aún.</div></div>}
        {plans.map((p) => {
          const st = STATUS_STYLES[p.status] || STATUS_STYLES.DRAFT;
          return (
            <Link key={p.id} to={`/app/admin/plans-v2/${p.id}`} style={{ display: "flex", padding: "14px 18px", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(255,255,255,0.05)", textDecoration: "none", color: "#fff" }} data-testid={`plan-v2-row-${p.id}`}>
              <div>
                <div style={{ fontWeight: 600 }}>{p.name}</div>
                <div style={{ fontSize: 12, color: "#a3a3a3", marginTop: 3 }}>
                  {clientName(p.client_id)} · v{p.version}
                  {p.parent_plan_id && <span> · <GitBranch size={11} style={{ verticalAlign: "middle" }}/> versión derivada</span>}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ background: st.bg, color: st.fg, padding: "3px 10px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", borderRadius: 999, fontWeight: 600 }}>{st.label}</span>
                <ChevronRight size={16} color="#a3a3a3"/>
              </div>
            </Link>
          );
        })}
      </div>
    </AppShell>
  );
}

const labelStyle = { display: "block", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: 5 };
const inputStyle = { width: "100%", background: "#111", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", padding: "9px 12px", fontSize: 14, borderRadius: 3, outline: "none", boxSizing: "border-box" };
const btnPrimary = { display: "inline-flex", alignItems: "center", gap: 6, background: "#dc2626", color: "#fff", border: "none", padding: "10px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", borderRadius: 2, whiteSpace: "nowrap" };
